import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  BuildingStorefrontIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useModal } from 'context/ModalContext';
import { useToast } from 'context/toastContext';
import moment from 'moment';
import normalizeEventDate from 'utils/normalizeEventDate';
import { getEventDateRange } from 'utils/eventDateRange';
import getEventStatus from 'utils/eventStatus';
import { useReveal, useCountUp, useFollowMouse, cx } from './hooks';
import { PrimaryBtn, GhostBtn, Eyebrow, Chip } from './atoms';
import EventCardWrapper from 'components/EventCardWrapper';
import useEasypayGate from 'hooks/useEasypayGate';
import { EasypayLockedHint } from 'components/EasypayPayout';
import HeroSearch from './HeroSearch';
import heroBg from './assets/there2b_background_elements_full_vector.svg';
import heroMarket from './assets/hero-market.jpg';

const HOMEPAGE_CATEGORIES = [
  { macroId: 1, emoji: '🍴', label: 'Food', tint: 'bg-rose-100 text-rose-700' },
  {
    macroId: 1,
    emoji: '🍷',
    label: 'Drinks',
    tint: 'bg-amber-100 text-amber-700',
  },
  {
    macroId: 2,
    emoji: '👜',
    label: 'Fashion',
    tint: 'bg-violet-100 text-violet-700',
  },
  {
    macroId: 6,
    emoji: '💄',
    label: 'Beauty',
    tint: 'bg-pink-100 text-pink-700',
  },
  {
    macroId: 5,
    emoji: '🎨',
    label: 'Art',
    tint: 'bg-indigo-100 text-indigo-700',
  },
  { macroId: 9, emoji: '🎶', label: 'Music', tint: 'bg-sky-100 text-sky-700' },
  {
    macroId: 3,
    emoji: '🛋️',
    label: 'Home',
    tint: 'bg-stone-100 text-stone-700',
  },
  {
    macroId: 6,
    emoji: '🌿',
    label: 'Wellness',
    tint: 'bg-emerald-100 text-emerald-700',
  },
];

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
function eventDateLabel(e) {
  const r = getEventDateRange(normalizeEventDate(e?.date));
  if (!r) return '';
  const s = moment(r.startDate);
  const en = moment(r.endDate);
  const startLbl = `${MONTHS[s.month()]} ${s.year()}`;
  const endLbl = `${MONTHS[en.month()]} ${en.year()}`;
  // e.g. "Jan 2026" (same month) or "Jan 2026 - Mar 2026" (range)
  return startLbl === endLbl ? startLbl : `${startLbl} - ${endLbl}`;
}

// ─────────────────────────────────────────────────────────────
// Hero — v1 editorial
// ─────────────────────────────────────────────────────────────
export function HeroSection({ accent = '#FFBD59', stats }) {
  const { t } = useTranslation();
  const [containerRef, mouse] = useFollowMouse();
  const [tab, setTab] = useState('events');

  const lines = [
    t('homepage.hero_line_1'),
    t('homepage.hero_line_2'),
    t('homepage.hero_line_3'),
  ];

  return (
    <section ref={containerRef} className="relative z-20 bg-brand/20">
      {/* Decorations are clipped here so the section itself can let the
          search dropdowns overflow above the following sections. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="blob"
          style={{
            left: mouse.x,
            top: mouse.y,
            opacity: mouse.inside ? 0.9 : 0.45,
            background: `radial-gradient(closest-side, ${accent}cc, transparent 70%)`,
          }}
        />
        <div className="absolute inset-0 grain opacity-40" />
        {/* Brand background motif — designer's full composition, decorative only */}
        <img
          src={heroBg}
          alt=""
          aria-hidden="true"
          className="absolute top-0 left-0 w-full h-auto select-none"
        />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-8 pt-4 pb-16 lg:pt-10 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 relative z-10 pt-1 lg:pt-6">
          <Eyebrow>{t('homepage.hero_eyebrow')}</Eyebrow>

          <h1
            className="mt-3 lg:mt-5 font-sans font-extrabold tracking-[-0.02em] text-ink-strong leading-[0.95]"
            style={{ fontSize: 'clamp(30px, 5.8vw, 76px)' }}
          >
            {lines.map((line, li) => (
              <span
                key={li}
                className="block"
                style={{ paddingBottom: '0.15em' }}
              >
                {line.split(' ').map((w, wi) => {
                  const isLastWord = wi === line.split(' ').length - 1;
                  const isLastLine = li === lines.length - 1;
                  return (
                    <span
                      key={wi}
                      className="word-in"
                      style={{
                        animationDelay: `${0.15 + li * 0.15 + wi * 0.05}s`,
                        marginRight: '0.22em',
                      }}
                    >
                      {isLastLine && isLastWord ? (
                        <span
                          className="italic font-serif font-medium text-yellow-03"
                          style={{ fontSize: '1.05em' }}
                        >
                          {w}
                        </span>
                      ) : (
                        <span>{w}</span>
                      )}
                    </span>
                  );
                })}
              </span>
            ))}
          </h1>

          <p className="mt-4 text-[13px] max-w-[40ch] leading-snug text-ink-strong/80 lg:mt-7 lg:text-[17px] lg:max-w-[52ch] lg:leading-relaxed">
            {t('homepage.hero_sub')}
          </p>

          <HeroSearch tab={tab} setTab={setTab} />

          {/* Desktop stats — sit under the search; imagery is the right column. */}
          {stats && stats.length > 0 && (
            <div className="mt-12 hidden lg:flex items-center flex-wrap gap-x-6 gap-y-4 sm:gap-7 text-[12px]">
              {stats.map((n, i) => (
                <HeroStat key={i} {...n} />
              ))}
            </div>
          )}

          {/* Mobile (T2B-390) — a 2-column stats grid on the left (brands +
              events, then bookings) and a compact imagery collage on the right
              whose toasts overhang toward the stats. The section's bottom
              padding leaves room below before section 2 of the homepage. */}
          <div className="mt-4 grid grid-cols-2 gap-x-2 items-start lg:hidden">
            {stats && stats.length > 0 && (
              <div className="grid grid-cols-2 gap-x-3 gap-y-8 pt-2">
                {stats.map((n, i) => (
                  <HeroStat key={i} {...n} />
                ))}
              </div>
            )}
            <div className="relative -mr-2">
              <HeroFloatingCardsMobile accent={accent} />
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-5 relative h-[640px]">
          <HeroFloatingCards accent={accent} />
        </div>
      </div>
    </section>
  );
}

function HeroStat({ value, suffix, label }) {
  const [ref, v] = useCountUp(value);
  return (
    <div ref={ref} className="flex mt-4 flex-col">
      <span className="text-[26px] font-extrabold tracking-tight text-ink-strong tabular-nums leading-none">
        {v}
        {suffix}
      </span>
      <span className="mt-1 text-[11px] text-gray-500 font-semibold uppercase tracking-[0.08em]">
        {label}
      </span>
    </div>
  );
}

// A single live photo card with a "LIVE" chip. Tilt comes from the float-* animation.
// `children` render as overlays on top of the photo (e.g. an inline booking toast).
function HeroPhotoCard({
  src,
  aspect = 'aspect-[3/4]',
  className = '',
  children,
}) {
  return (
    <div
      className={cx(
        'rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-lift bg-ink-strong',
        className
      )}
    >
      <div className={cx(aspect, 'relative')}>
        <img
          src={src}
          className="absolute inset-0 w-full h-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3">
          <Chip tone="emerald" size="xs">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 softpulse" />{' '}
            LIVE
          </Chip>
        </div>
        {children}
      </div>
    </div>
  );
}

// A floating notification "toast" (booking / partner confirmed).
function HeroNotifCard({ accent, label, title, sub }) {
  return (
    <div className="rounded-2xl bg-white shadow-lift ring-1 ring-line p-4 flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
        style={{ background: accent }}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="#1F2937"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-emerald-700 font-bold uppercase tracking-[0.1em]">
          {label}
        </p>
        <p className="text-[13.5px] text-ink-strong font-bold tracking-tight leading-tight">
          {title}
        </p>
        <p className="text-[11.5px] text-gray-500 mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function HeroFloatingCards({ accent }) {
  // Decorative only. Top photo is a generic stock boutique interior.
  const interiorCover =
    'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?auto=format&fit=crop&w=900&q=70';
  return (
    <div className="absolute inset-0">
      {/* Top-right photo — the original, stays on top */}
      <div className="absolute top-2 right-2 w-[260px] z-10 float-a">
        <HeroPhotoCard src={interiorCover} />
      </div>

      {/* Booking toast — overlaps the lower-left of the top photo */}
      <div className="absolute top-[244px] left-0 w-[290px] z-30 float-b">
        <HeroNotifCard
          accent={accent}
          label="Booking confirmed"
          title="Loft 84 · Designers Fair"
          sub="Stall #B12 · Jul 4–5"
        />
      </div>

      {/* Market photo — landscape, below the original. Plain image; LIVE badge,
          booking toast and the left-high tilt (float-b) are all rendered here.
          The toast lives outside the photo's clip box so it overflows on top. */}
      <div className="absolute top-[342px] left-0 w-[360px] z-20 float-b">
        <div className="relative">
          <HeroPhotoCard
            src={heroMarket}
            aspect="aspect-[4/3]"
            className="rounded-2xl"
          />
          <div className="absolute bottom-[-18px] right-[-26px] w-[306px] z-10">
            <HeroNotifCard
              accent={accent}
              label="Booking request sent"
              title="Open-Air Fashion Market · Stand Cluster A"
              sub="Weekend Pop-up Activation"
            />
          </div>
        </div>
      </div>

      <svg
        className="absolute -bottom-10 -left-16 w-[280px] h-[280px] opacity-30"
        viewBox="0 0 100 100"
      >
        <defs>
          <linearGradient id="home-hero-arc" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={accent} />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <circle
          cx="50"
          cy="50"
          r="48"
          stroke="url(#home-hero-arc)"
          strokeWidth="0.6"
          fill="none"
        />
      </svg>
    </div>
  );
}

// Compact booking toast for the mobile hero collage (T2B-390).
function MobileNotif({ accent, label, title, sub }) {
  return (
    <div className="rounded-xl bg-white shadow-lift ring-1 ring-line px-2 py-1.5 flex items-start gap-1.5">
      <div
        className="w-4 h-4 rounded-md grid place-items-center shrink-0"
        style={{ background: accent }}
      >
        <svg
          viewBox="0 0 24 24"
          width="9"
          height="9"
          fill="none"
          stroke="#1F2937"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[4px] text-emerald-700 font-bold uppercase tracking-[0.05em] leading-tight">
          {label}
        </p>
        <p className="mt-0.5 text-[6.5px] text-ink-strong font-bold tracking-tight leading-tight whitespace-nowrap">
          {title}
        </p>
        <p className="mt-0.5 text-[5.5px] text-gray-500 leading-tight truncate">
          {sub}
        </p>
      </div>
    </div>
  );
}

// Mobile-only hero imagery (T2B-390): a vertical stack of two live photo cards
// with overlapping booking toasts, sized to sit beside the stats column.
function HeroFloatingCardsMobile({ accent }) {
  const interiorCover =
    'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?auto=format&fit=crop&w=600&q=70';
  return (
    <div className="relative mt-4 ml-auto w-[168px]">
      {/* Upper photo — portrait, tilted, sits on the right. Rotation is on the
          outer wrapper so it doesn't fight the float animation's transform. */}
      <div className="w-[106px] ml-14 rotate-[-2deg]">
        <div className="float-a m4">
          <HeroPhotoCard
            src={interiorCover}
            aspect="aspect-[3/4]"
            className="rounded-2xl"
          />
        </div>
      </div>
      {/* Booking-confirmed toast — tilted right to match the photos; overhangs
          the photo's lower-left. */}
      <div className="-mt-10 -ml-8 w-[136px] relative z-30 rotate-[6deg]">
        <MobileNotif
          accent={accent}
          label="Booking confirmed"
          title="Loft 84 · Designers Fair"
          sub="Stall #B12 · Jul 4–5"
        />
      </div>
      {/* Lower cluster — staggered well to the LEFT of the upper photo (per the
          design), dropping into the empty space below the stats column. */}
      <div className="mt-1 -ml-10">
        <div className="w-[164px] rotate-[6deg]">
          <div className="float-c">
            <HeroPhotoCard
              src={heroMarket}
              aspect="aspect-[4/3]"
              className="rounded-2xl"
            />
          </div>
        </div>
        {/* Its toast — tilted right to match, pulled up to sit on top of the
            photo's lower half (per the design). */}
        <div className="-mt-10 ml-6 w-[148px] relative z-30 rotate-[6deg]">
          <MobileNotif
            accent={accent}
            label="Booking request sent"
            title="Open-Air Fashion Market"
            sub="Weekend Pop-up · Cluster A"
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Ticker — marquee of live + upcoming events from real data
// ─────────────────────────────────────────────────────────────
export function TickerSection({ events = [] }) {
  const { t } = useTranslation();
  // Availability is for signed-in eyes only, here as everywhere else: the
  // scarcity pills ("2 left") stay out of the marquee for guests.
  const { isAuthenticated } = useSelector((state) => state.auth || {});
  const items = useMemo(() => [...events, ...events], [events]);
  if (!events.length) return null;
  return (
    <section className="bg-ink-black text-white py-4 border-y border-white/10">
      <div className="max-w-[1280px] mx-auto px-6 md:px-8 flex items-center gap-6">
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <span className="w-2 h-2 rounded-full bg-emerald-400 softpulse" />
          <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-white/70">
            {t('homepage.ticker_label')}
          </span>
        </div>
        <div className="flex-1 overflow-hidden mask-fade-x">
          <div className="marquee-track flex items-center gap-3 w-max">
            {items.map((e, i) => (
              <TickerItem
                key={`${e.id}-${i}`}
                event={e}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TickerItem({ event, isAuthenticated }) {
  const navigate = useNavigate();
  const status = getEventStatus(event?.date);
  const isLive = status === 'live';
  const total = Number(event?.totalSpaces) || 0;
  const booked = Number(event?.bookedSpaces) || 0;
  const left = total > 0 ? total - booked : null;
  // Guests never see how full an event is — they fall back to the plain date
  // pill, same as any event that isn't running low.
  const almost = isAuthenticated && left != null && left > 0 && left <= 3;

  const kind = isLive ? 'live' : almost ? 'almost' : 'soon';
  const palette =
    kind === 'live'
      ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30'
      : kind === 'almost'
        ? 'bg-rose-500/15 text-rose-300 ring-rose-400/30'
        : 'bg-white/5 text-white/80 ring-white/15';
  const dot =
    kind === 'live' ? (
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 softpulse" />
    ) : kind === 'almost' ? (
      <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
    ) : (
      <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
    );
  const when = isLive ? 'NOW' : almost ? `${left} left` : eventDateLabel(event);

  const handleClick = () => {
    if (event?.slug) navigate(`/event/${event.slug}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!event?.slug}
      className={cx(
        'inline-flex items-center gap-2 px-3.5 py-2 rounded-full ring-1 whitespace-nowrap transition hover:ring-white/50 hover:brightness-110',
        event?.slug ? 'cursor-pointer' : 'cursor-default',
        palette
      )}
    >
      <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] font-bold">
        {dot}
        {when}
      </span>
      <span className="w-px h-3 bg-white/20" />
      <span className="text-[12.5px] font-bold tracking-tight text-white">
        {event?.name}
      </span>
      {event?.city && (
        <span className="text-[11.5px] text-white/50">· {event.city}</span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Categories
// ─────────────────────────────────────────────────────────────
export function CategoriesSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <section className="py-14 bg-paper-2">
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <Eyebrow>{t('homepage.categories_eyebrow')}</Eyebrow>
            <h2 className="mt-2 text-[28px] font-extrabold tracking-tight text-ink-strong">
              {t('homepage.categories_title')}
            </h2>
          </div>
          <GhostBtn onClick={() => navigate('/listings?searchType=events')}>
            {t('homepage.categories_see_all')}
          </GhostBtn>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {HOMEPAGE_CATEGORIES.map((c, i) => (
            <CategoryTile key={i} {...c} delay={i * 40} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryTile({ emoji, label, tint, macroId, delay }) {
  const [ref, seen] = useReveal();
  const navigate = useNavigate();
  return (
    <button
      ref={ref}
      type="button"
      onClick={() =>
        navigate(`/listings?searchType=events&macroCategories=${macroId}`)
      }
      className={cx(
        'reveal lift rounded-2xl bg-white ring-1 ring-line p-4 flex flex-col items-center justify-center gap-2 aspect-square',
        seen && 'in'
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <span
        className={cx(
          'w-10 h-10 rounded-xl grid place-items-center text-[20px]',
          tint
        )}
      >
        {emoji}
      </span>
      <span className="text-[12px] font-bold tracking-tight text-ink-strong">
        {label}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Featured events
// ─────────────────────────────────────────────────────────────
export function FeaturedSection({ events = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!events.length) return null;
  return (
    <section className="py-14 sm:py-20 bg-white">
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        <div className="fade-soft flex flex-col items-center text-center mb-8 gap-5">
          <div>
            <h2
              className="font-sans font-extrabold tracking-tight text-ink-strong leading-[1.05] whitespace-nowrap"
              style={{ fontSize: 'clamp(20px, 5.4vw, 40px)' }}
            >
              {t('homepage.featured_title_a')}{' '}
              <span className="italic font-serif font-medium text-brand-deep">
                {t('homepage.featured_title_b')}
              </span>
              <span className="text-brand-deep">.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-fr items-stretch">
          {events.slice(0, 4).map((ev, i) => (
            <FeaturedTile key={ev.id} ev={ev} delay={i * 80} />
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <PrimaryBtn onClick={() => navigate('/listings?searchType=events')}>
            {t('homepage.featured_see_all')}
          </PrimaryBtn>
        </div>
      </div>
    </section>
  );
}

function FeaturedTile({ ev, delay }) {
  const [ref, seen] = useReveal();
  const { isAuthenticated } = useSelector((state) => state.auth || {});
  return (
    <div
      ref={ref}
      className={cx('reveal h-full', seen && 'in')}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <EventCardWrapper
        event={ev}
        hasNavigation
        isAuthenticated={isAuthenticated}
        hideDaysPill
        smallLogo
        equalTitleHeight
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Split — brands × promoters × everyone
// ─────────────────────────────────────────────────────────────
export function SplitSection({ accent = '#FFBD59' }) {
  const { t } = useTranslation();
  const [ref, seen] = useReveal();
  return (
    <section
      ref={ref}
      className="py-14 sm:py-20 bg-yellow-03/20 relative overflow-hidden"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        <div className={cx('reveal text-center mx-auto mb-12', seen && 'in')}>
          <Eyebrow>{t('homepage.split_eyebrow')}</Eyebrow>
          <h2
            className="mt-2 font-sans font-extrabold tracking-tight text-ink-strong leading-[1.05] whitespace-nowrap"
            style={{ fontSize: 'clamp(18px, 5.2vw, 42px)' }}
          >
            {t('homepage.split_title_a')}{' '}
            <span
              className="italic font-serif font-medium"
              style={{ color: accent }}
            >
              {t('homepage.split_title_b')}
            </span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SplitCard side="brand" />
          <SplitCard side="promoter" />
          <SplitCard side="everyone" />
        </div>
      </div>
    </section>
  );
}

const SPLIT_CARDS = {
  brand: {
    key: 'split_brand',
    Icon: BuildingStorefrontIcon,
    dark: false,
    card: 'bg-brand ring-black/5',
    num: 'bg-white/50 text-ink-strong',
    title: 'text-ink-strong',
    copy: 'text-ink-strong/70',
    icon: 'text-ink-strong',
    btnTone: 'white',
  },
  promoter: {
    key: 'split_promoter',
    Icon: MapPinIcon,
    dark: true,
    card: 'bg-ink-strong text-white ring-black/30',
    num: 'bg-white/10 text-white ring-1 ring-white/15',
    title: 'text-white',
    copy: 'text-white/70',
    icon: 'text-white',
    btnTone: 'amber',
  },
  everyone: {
    key: 'split_everyone',
    Icon: MagnifyingGlassIcon,
    dark: false,
    card: 'bg-white ring-line',
    num: 'bg-paper text-ink-strong ring-1 ring-line',
    title: 'text-ink-strong',
    copy: 'text-gray-600',
    icon: 'text-ink-strong',
    btnTone: 'brand',
  },
};

function SplitCard({ side }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showLoginModal } = useModal();
  const addToast = useToast();
  const { isAuthenticated, user } = useSelector((state) => state.auth || {});
  const isPromoter = (user?.role || '') === 'promoter';
  const { blocked: easypayBlocked } = useEasypayGate();
  const [ref, seen] = useReveal();

  const cfg = SPLIT_CARDS[side];
  const { key: k, Icon } = cfg;
  const steps = [1, 2, 3].map((n) => ({
    n: `0${n}`,
    title: t(`homepage.${k}_step_${n}_title`),
    copy: t(`homepage.${k}_step_${n}_copy`),
  }));

  const goToSearch = (searchType) => {
    const params = new URLSearchParams();
    params.set('searchType', searchType);
    navigate(`/listings?${params.toString()}`);
  };

  const onCta = () => {
    if (side === 'brand') return goToSearch('events');
    if (side === 'everyone') return goToSearch('brands');
    // promoter → create a place (event creation). Only promoters can create
    // events: guests get the login modal, authenticated non-promoters (brands)
    // get an explanatory toast.
    if (!isAuthenticated) return showLoginModal('/create-event');
    if (isPromoter) return navigate('/create-event');
    return addToast(t('homepage.split_promoter_cta_promoter_only'), 'neutral');
  };

  // T2B-423: same treatment as every other "create an event" control — the
  // button works (the event is saved as a draft) and the "i" beside it says
  // publishing waits for Easypay. Only for a signed-in promoter with no
  // Easypay account; guests and brands see the card as before.
  const ctaHint = side === 'promoter' && isPromoter && easypayBlocked;

  return (
    <div
      ref={ref}
      className={cx(
        'reveal lift rounded-3xl overflow-hidden p-6 sm:p-10 shadow-card ring-1 relative flex flex-col h-full',
        seen && 'in',
        cfg.card
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <Eyebrow
          tone={cfg.dark ? 'light' : 'dark'}
          dotClass={side === 'brand' ? 'bg-white' : 'bg-brand'}
        >
          {t(`homepage.${k}_who`)}
        </Eyebrow>
        <span className={cx('relative shrink-0', cfg.icon)} aria-hidden>
          <Icon className="w-9 h-9" strokeWidth={1.5} />
          {side === 'everyone' && (
            <SparklesIcon
              className="w-4 h-4 absolute -top-1.5 -right-1.5"
              strokeWidth={1.5}
            />
          )}
        </span>
      </div>

      <h3
        className={cx(
          'mt-3 font-extrabold tracking-tight leading-[1.1] whitespace-pre-line text-balance',
          cfg.title
        )}
        style={{ fontSize: 'clamp(20px, 3.4vw, 28px)' }}
      >
        {t(`homepage.${k}_title`)}
      </h3>

      <ul className="mt-7 space-y-4">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-4">
            <span
              className={cx(
                'shrink-0 w-9 h-9 rounded-full grid place-items-center text-[12px] font-extrabold tabular-nums',
                cfg.num
              )}
            >
              {s.n}
            </span>
            <div className="flex-1">
              <p
                className={cx(
                  'text-[14px] font-extrabold tracking-tight',
                  cfg.title
                )}
              >
                {s.title}
              </p>
              <p
                className={cx('text-[12.5px] leading-relaxed mt-0.5', cfg.copy)}
              >
                {s.copy}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-9 flex items-center gap-2">
        <PrimaryBtn tone={cfg.btnTone} onClick={onCta}>
          {t(`homepage.${k}_cta`)}
        </PrimaryBtn>
        {ctaHint && (
          // Upward and left-aligned: the CTA sits at the bottom-left of the
          // card, so the toolbars' below-and-right default would open the
          // panel past the card's edge and toward the fold.
          <EasypayLockedHint placement="top" align="center" tone="light" />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Manifesto
// ─────────────────────────────────────────────────────────────
export function ManifestoSection({ accent = '#FFBD59' }) {
  const { t } = useTranslation();
  const [ref, seen] = useReveal();
  const triplet = [
    { word: t('homepage.anyone'), copy: t('homepage.anyone_description') },
    { word: t('homepage.anywhere'), copy: t('homepage.anywhere_description') },
    { word: t('homepage.anytime'), copy: t('homepage.anytime_description') },
  ];

  return (
    <section
      ref={ref}
      className="relative bg-ink-black text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-60 pointer-events-none">
        <div
          className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full"
          style={{
            background: `radial-gradient(closest-side, ${accent}22, transparent 70%)`,
          }}
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left — vision statement */}
        <div className={cx('lg:col-span-5 reveal', seen && 'in')}>
          <Eyebrow tone="light">{t('homepage.manifesto_eyebrow')}</Eyebrow>
          <h2
            className="mt-4 font-extrabold tracking-[-0.02em] leading-[0.98]"
            style={{ fontSize: 'clamp(40px, 8vw, 64px)' }}
          >
            <span className="block">{t('homepage.manifesto_title_a')}</span>
            <span
              className="block italic font-serif font-medium"
              style={{ color: accent, fontSize: '1.1em' }}
            >
              {t('homepage.manifesto_title_b')}
            </span>
          </h2>

          <span
            className="block w-14 h-1 mt-8 mb-8 rounded-full"
            style={{ background: accent }}
          />

          {[1, 2, 3].map((i) => (
            <p
              key={i}
              className="text-[16px] leading-relaxed text-white/70 mb-5 max-w-[44ch]"
            >
              {t(`homepage.manifesto_body_${i}`)}
            </p>
          ))}
        </div>

        {/* Right — anyone / anywhere / anytime cards */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          {triplet.map((tr, i) => (
            <ManifestoTriplet key={i} {...tr} idx={i} accent={accent} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ManifestoTriplet({ word, copy, idx, accent }) {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={cx(
        'reveal group rounded-2xl border border-white/10 bg-white/[0.015]',
        'px-6 py-8 sm:px-10 sm:py-9 transition-colors hover:border-white/20',
        'flex flex-col gap-5 sm:grid sm:grid-cols-[auto_auto_minmax(11rem,16rem)_minmax(0,1fr)] sm:items-center sm:gap-6',
        seen && 'in'
      )}
      style={{ transitionDelay: `${0.15 + idx * 0.12}s` }}
    >
      {/* Number */}
      <span className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full grid place-items-center border border-white/15 text-[14px] font-bold tabular-nums text-white/70">
        0{idx + 1}
      </span>

      {/* Vertical divider with glowing dot */}
      <div className="relative hidden sm:block shrink-0 w-px h-20">
        <span className="absolute inset-0 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
        <span
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
          style={{ background: accent, boxShadow: `0 0 12px 2px ${accent}aa` }}
        />
      </div>

      {/* Word */}
      <h3
        className="min-w-0 italic font-serif font-medium leading-[1.05] text-balance"
        style={{ color: accent, fontSize: 'clamp(28px, 3vw, 38px)' }}
      >
        {word}.
      </h3>

      {/* Description */}
      <p className="min-w-0 text-[14px] text-white/55 leading-[1.6]">{copy}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Brands strip
// ─────────────────────────────────────────────────────────────
export function BrandsSection({ brands = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ref, seen] = useReveal();
  if (!brands.length) return null;
  return (
    <section ref={ref} className="py-14 sm:py-20 bg-white">
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        <div
          className={cx(
            'reveal flex items-end justify-between mb-8 gap-6 flex-wrap',
            seen && 'in'
          )}
        >
          <div>
            <Eyebrow>{t('homepage.brands_eyebrow')}</Eyebrow>
            <h2
              className="mt-2 font-extrabold tracking-tight text-ink-strong leading-[1.05] max-w-[20ch]"
              style={{ fontSize: 'clamp(28px, 5.4vw, 40px)' }}
            >
              {t('homepage.brands_title_a')}{' '}
              <span className="italic font-serif font-medium text-brand-deep">
                {t('homepage.brands_title_b')}
              </span>
              .
            </h2>
          </div>
          <GhostBtn onClick={() => navigate('/listings?searchType=brands')}>
            {t('homepage.brands_see_all')}
          </GhostBtn>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.slice(0, 6).map((b, i) => (
            <BrandCard key={b.id || i} brand={b} delay={i * 60} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandCard({ brand, delay }) {
  const { t } = useTranslation();
  const [ref, seen] = useReveal();
  const navigate = useNavigate();
  const name = brand?.name || brand?.brandName || '';
  const tag = brand?.tagline || brand?.description || '';
  const avatar = brand?.avatar || brand?.logo;
  const events = brand?.eventsCount ?? brand?.confirmedEventsCount ?? null;
  const onClick = () => {
    if (brand?.slug) navigate(`/brands/${brand.slug}`);
  };
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cx(
        'reveal lift rounded-2xl bg-paper-2 ring-1 ring-line p-5 flex items-center gap-4 text-left',
        seen && 'in'
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {avatar ? (
        <img
          src={avatar}
          alt=""
          className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm"
        />
      ) : (
        <div className="w-14 h-14 rounded-full bg-brand-soft ring-2 ring-white shadow-sm grid place-items-center text-ink-strong font-extrabold text-lg">
          {name?.[0] || '?'}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[15px] text-ink-strong font-extrabold tracking-tight leading-none truncate">
          {name}
        </p>
        {tag && (
          <p className="text-[11.5px] text-gray-500 mt-1 line-clamp-2">{tag}</p>
        )}
      </div>
      {events != null && (
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-400">
            {t('homepage.brands_booked_at')}
          </p>
          <p className="text-[18px] font-extrabold tabular-nums text-ink-strong leading-none mt-0.5">
            {events}
            <span className="text-gray-400 text-[12px] font-bold">
              {' '}
              {t('homepage.brands_events_suffix')}
            </span>
          </p>
        </div>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// CTA
// ─────────────────────────────────────────────────────────────
export function CTASection({ accent = '#FFBD59' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ref, seen] = useReveal();
  return (
    <section
      ref={ref}
      className="py-32 sm:py-52 bg-paper-warm relative overflow-hidden"
    >
      <svg
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(1600px,170vw)] h-[min(1600px,170vw)] opacity-[0.04] pointer-events-none"
        viewBox="0 0 200 200"
      >
        {Array.from({ length: 28 }).map((_, i) => (
          <circle
            key={i}
            cx="100"
            cy="100"
            r={12 + i * 4}
            stroke="#1F2937"
            strokeWidth=".2"
            fill="none"
          />
        ))}
      </svg>

      <div
        className={cx(
          'relative max-w-[1280px] mx-auto px-6 md:px-8 text-center reveal',
          seen && 'in'
        )}
      >
        <h2
          className="font-extrabold tracking-[-0.02em] text-ink-strong leading-[0.98] max-w-[20ch] mx-auto"
          style={{ fontSize: 'clamp(36px, 7.5vw, 64px)' }}
        >
          {t('homepage.cta_title_a')}
          <br />
          <span
            className="italic font-serif font-medium"
            style={{ color: accent }}
          >
            {t('homepage.cta_title_b')}
          </span>
        </h2>
        <p className="mt-5 text-[16px] text-gray-600 max-w-[60ch] mx-auto">
          {t('homepage.cta_sub')}
        </p>
        <div className="mt-11 flex items-center justify-center gap-3 flex-wrap">
          <PrimaryBtn
            tone="amber"
            size="lg"
            onClick={() => navigate('/register')}
          >
            {t('homepage.cta_primary')}
          </PrimaryBtn>
          <PrimaryBtn
            tone="white"
            size="lg"
            icon={false}
            onClick={() => navigate('/listings?searchType=events')}
          >
            {t('homepage.cta_secondary')}
          </PrimaryBtn>
        </div>
      </div>
    </section>
  );
}
